import React, { createContext, ReactNode, useContext, useEffect, useState } from "react";
import { useLocation,useNavigate } from "react-router";

export type SettingsSection = "integrations" | "teams" | "network" | "usage" | "audit-logs" | "billing" | "cost" | "value";

interface SettingsContextType {
  activeSection: SettingsSection;
  setActiveSection: (section: SettingsSection) => void;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export function SettingsProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();

  // Determine the active section from the URL path
  const getActiveSectionFromPath = (): SettingsSection => {
    const path = location.pathname;
    if (path.includes("/settings/integrations")) return "integrations";
    if (path.includes("/settings/teams")) return "teams";
    if (path.includes("/settings/network")) return "network";
    if (path.includes("/settings/audit-logs")) return "audit-logs";
    if (path.includes("/settings/usage")) return "usage";
    if (path.includes("/settings/billing")) return "billing";
    if (path.includes("/settings/cost")) return "cost";
    if (path.includes("/settings/value")) return "value";
    return "integrations";
  };

  // State for active section
  const [activeSection, setActiveSectionState] = useState<SettingsSection>(
    getActiveSectionFromPath()
  );

  // Update active section when URL changes
  useEffect(() => {
    setActiveSectionState(getActiveSectionFromPath());
  }, [location.pathname]);

  // Function to change sections (navigates to appropriate URL)
  const setActiveSection = (section: SettingsSection) => {
    navigate(`/settings/${section}`);
  };

  return (
    <SettingsContext.Provider value={{ activeSection, setActiveSection }}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error("useSettings must be used within a SettingsProvider");
  }
  return context;
}
